import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './SplitScrollTable.module.scss';
import { useMilestonesGridSlice } from './slice';
import {
  selectMilestonesData,
  selectMilestonesLoading,
  selectMilestonesInitialLoad,
  selectMilestonesError,
  selectIsMetaTableCollapsed,
  selectColumnConfig
} from './slice/selectors';
import {
  selectDateRange,
  selectFullScreenMode
} from 'app/pages/Home/slice/selectors';
import { useScrollSync } from './hooks/useScrollSync';
import { useRowInteractions } from './hooks/useRowInteractions';
import { useFilteredMilestones } from './hooks/useFilteredMilestones';
import { useSortedMilestones } from './hooks/useSortedMilestones';
import { MetaTable } from './components/MetaTable';
import { MilestonesTable } from './components/MilestonesTable';
import { BottomScrollbar } from './components/BottomScrollbar';
import { TableStates } from './components/TableStates';
import { milestonesColumnsConfig } from './TableConfig/milestonesColumnsConfig';
import { MilestoneColumnConfig } from './types';
import {
  MILESTONE_GRID_COLUMN_WIDTH_PX,
  PHASE_DATE_COLUMN_WIDTH_PX
} from './constants/milestoneGridLayout';
// import { sampleMilestonesData } from './sampleData';

interface SplitScrollTableProps {
  showPhaseDatesOnly?: boolean;
}

const SplitScrollTable: React.FC<SplitScrollTableProps> = ({
  showPhaseDatesOnly = false
}) => {
  const dispatch = useDispatch();
  const { actions } = useMilestonesGridSlice();

  // Selectors
  const rawMilestonesData = useSelector(selectMilestonesData);
  // const rawMilestonesData = sampleMilestonesData;
  const loading = useSelector(selectMilestonesLoading);
  const initialLoad = useSelector(selectMilestonesInitialLoad);
  const error = useSelector(selectMilestonesError);
  const dateRange = useSelector(selectDateRange);
  const isLeftTableCollapsed = useSelector(selectIsMetaTableCollapsed);
  const isFullScreenMode = useSelector(selectFullScreenMode);
  const apiColumnConfig = useSelector(selectColumnConfig);

  // Extract date values to avoid object reference issues
  const startDate = dateRange?.start;
  const endDate = dateRange?.end;

  // Apply all filters to the data with AND logic
  const filteredMilestonesData = useFilteredMilestones(rawMilestonesData);

  // Apply sorting AFTER filtering for better performance
  // For now, sorting is applied to the raw data (when filters are enabled, replace rawMilestonesData with filteredMilestonesData)
  const milestonesData = useSortedMilestones(filteredMilestonesData);

  // Refs
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomScrollbarRef = useRef<HTMLDivElement>(null);

  // Custom hooks
  const {
    hoveredRow,
    expandedRows,
    handleRowHover,
    handleRowLeave,
    toggleRow
  } = useRowInteractions();

  // Shared chunking state for synchronized rendering across both tables
  const [renderedChunks, setRenderedChunks] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Handle touchpad icon click
  const handleTouchPadClick = (columnKey: string) => {
    dispatch(actions.toggleMetaTableCollapsed());
  };

  const { handleTableScroll, handleBottomScrollbarScroll } = useScrollSync({
    scrollContainerRef,
    bottomScrollbarRef,
    milestonesData
  });

  // Resolve column config: prefer API-driven config, fall back to static
  const resolvedColumnsConfig: MilestoneColumnConfig[] = useMemo(
    () =>
      apiColumnConfig && apiColumnConfig.length > 0
        ? apiColumnConfig.filter(c => c.visible !== false)
        : (milestonesColumnsConfig as unknown as MilestoneColumnConfig[]),
    [apiColumnConfig]
  );

  // Load data and column config on mount; reload data when date range changes
  useEffect(() => {
    dispatch(actions.loadColumnConfig());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(actions.loadMilestonesData({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]); // Use primitive values instead of object reference

  const hasData = milestonesData?.data && milestonesData.data.length > 0;

  const columnsConfig = useMemo(() => {
    if (showPhaseDatesOnly) {
      return resolvedColumnsConfig.filter(column => column.phaseDate);
    }
    return resolvedColumnsConfig.filter(column => !column.phaseDate);
  }, [showPhaseDatesOnly, resolvedColumnsConfig]);

  /** Explicit widths so header/body cells match and table width matches column sum. */
  const columnsWithWidths = useMemo(
    () =>
      columnsConfig.map(column => {
        if (column.phaseDate) {
          return column;
        }
        return column.width
          ? column
          : {
              ...column,
              width: MILESTONE_GRID_COLUMN_WIDTH_PX
            };
      }),
    [columnsConfig]
  );

  const phaseColumnsConfig = useMemo(() => {
    return resolvedColumnsConfig.filter(column => column.phaseDate);
  }, [resolvedColumnsConfig]);

  const tableWidth = useMemo(() => {
    if (columnsConfig.length === 0) return 0;
    if (showPhaseDatesOnly) {
      return PHASE_DATE_COLUMN_WIDTH_PX * columnsConfig.length;
    }
    return MILESTONE_GRID_COLUMN_WIDTH_PX * columnsConfig.length;
  }, [columnsConfig.length, showPhaseDatesOnly]);

  const phaseTableWidth = useMemo(() => {
    return PHASE_DATE_COLUMN_WIDTH_PX * phaseColumnsConfig.length;
  }, [phaseColumnsConfig.length]);

  const totalTableWidth = useMemo(() => {
    if (showPhaseDatesOnly) {
      return tableWidth;
    }
    return tableWidth + phaseTableWidth;
  }, [phaseTableWidth, showPhaseDatesOnly, tableWidth]);

  return (
    <TableStates
      loading={loading}
      initialLoad={initialLoad}
      error={error}
      hasData={!!hasData}
    >
      <div
        className={`${styles.mainContainer} ${
          isFullScreenMode ? styles.fullscreen : ''
        }`}
        ref={mainContainerRef}
      >
        <div className={styles.flexContainer}>
          <MetaTable
            data={milestonesData?.data || []}
            itemCount={milestonesData?.data?.length || 0}
            hoveredRow={hoveredRow}
            expandedRows={expandedRows}
            rowHoveredClass={styles.rowHovered}
            onRowHover={handleRowHover}
            onRowLeave={handleRowLeave}
            onToggleRow={toggleRow}
            isCollapsed={isLeftTableCollapsed}
            onTouchPadClick={handleTouchPadClick}
            renderedChunks={renderedChunks}
            setRenderedChunks={setRenderedChunks}
            isLoadingMore={isLoadingMore}
            setIsLoadingMore={setIsLoadingMore}
          />

          <div className={styles.rightTableWrapper}>
            <MilestonesTable
              key={showPhaseDatesOnly ? 'phase-dates' : 'all-columns'}
              data={milestonesData?.data || []}
              columnsConfig={columnsWithWidths}
              tableWidth={tableWidth}
              showPhaseDatesOnly={showPhaseDatesOnly}
              appendPhaseDatesTable={!showPhaseDatesOnly}
              phaseColumnsConfig={phaseColumnsConfig}
              phaseTableWidth={phaseTableWidth}
              hoveredRow={hoveredRow}
              expandedRows={expandedRows}
              rowHoveredClass={styles.rowHovered}
              scrollContainerRef={scrollContainerRef}
              onScroll={handleTableScroll}
              onRowHover={handleRowHover}
              onRowLeave={handleRowLeave}
              onToggleRow={toggleRow}
              renderedChunks={renderedChunks}
              setRenderedChunks={setRenderedChunks}
              isLoadingMore={isLoadingMore}
              setIsLoadingMore={setIsLoadingMore}
            />

            <BottomScrollbar
              bottomScrollbarRef={bottomScrollbarRef}
              onScroll={handleBottomScrollbarScroll}
              contentWidth={totalTableWidth}
            />
          </div>
        </div>
      </div>
    </TableStates>
  );
};

export default SplitScrollTable;
